import {
	Box,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import type { SelectChangeEvent, SxProps, Theme } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from '../../../app/hooks';
import CustomButton from '../../../components/custom/btn/CustomButton';
import CustomSuccessButton from '../../../components/custom/btn/CustomSuccessButton';
import CustomSwitch from '../../../components/custom/controls/CustomSwitch';
import { toggleInlineRowSx } from '../../../components/custom/controls/customToggleStyles';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../components/custom/snackbar/snackbarSlice';
import {
	EXTERNAL_DATA_LAYER_ACCENT,
	EXTERNAL_DATA_LAYER_PALETTE,
} from '../../../shared/externalDataLayerColors';
import AdminSection from '../AdminSection';
import {
	ProviderSelectItems,
	providerSelectSx,
	renderProviderSelectValue,
} from '../teams/ProviderOptionLabel';
import { sortProvidersLiveFirst } from '../teams/teamProviderConstants';
import {
	ExternalDataLayer,
	ExternalDataLayerConfig,
	LayerAssignment,
	fetchExternalDataLayerConfig,
	patchExternalDataLayerConfig,
} from './externalDataAdminApi';

const LAYERS: ExternalDataLayer[] = ['SCHEDULE', 'ODDS', 'LIVE', 'FULL_MATCH', 'STANDINGS'];

const LAYER_LABEL_KEY: Record<ExternalDataLayer, string> = {
	SCHEDULE: 'externalDataLayerSchedule',
	ODDS: 'externalDataLayerOdds',
	LIVE: 'externalDataLayerLive',
	FULL_MATCH: 'externalDataLayerFullMatch',
	STANDINGS: 'externalDataLayerStandings',
};

const NONE = '';
const DEFAULT_ODDS_REFRESH_WITHIN_HOURS = 36;

function layerCardSx(layer: ExternalDataLayer): SxProps<Theme> {
	const pal = EXTERNAL_DATA_LAYER_PALETTE[layer];
	return (theme) => {
		const isDark = theme.palette.mode === 'dark';
		return {
			mb: 2,
			p: 1.5,
			borderRadius: 2,
			border: `1.5px solid ${isDark ? pal.borderDark : pal.border}`,
			background: isDark
				? `linear-gradient(155deg, ${pal.surfaceDark} 0%, rgba(15,23,42,0.85) 70%)`
				: `linear-gradient(155deg, ${pal.surface} 0%, #ffffff 70%)`,
			boxShadow: isDark ? 'none' : `0 4px 14px ${pal.soft}`,
			position: 'relative',
			overflow: 'hidden',
			'&::before': {
				content: '""',
				position: 'absolute',
				left: 0,
				top: 0,
				bottom: 0,
				width: 4,
				background: pal.accent,
			},
		};
	};
}

export default function ExternalDataLayersPanel(): JSX.Element {
	const dispatch = useAppDispatch();
	const [showPanel, setShowPanel] = useState(false);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [config, setConfig] = useState<ExternalDataLayerConfig | null>(null);
	const [draft, setDraft] = useState<Partial<Record<ExternalDataLayer, LayerAssignment>>>({});
	const [oddsRefreshWithinHours, setOddsRefreshWithinHours] = useState(DEFAULT_ODDS_REFRESH_WITHIN_HOURS);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const next = await fetchExternalDataLayerConfig();
			setConfig(next);
			setDraft(next.layers ?? {});
			setOddsRefreshWithinHours(
				next.oddsRefreshWithinHours != null && next.oddsRefreshWithinHours > 0
					? next.oddsRefreshWithinHours
					: DEFAULT_ODDS_REFRESH_WITHIN_HOURS
			);
		} catch (error) {
			dispatch(
				showErrorSnackbar({
					message: error instanceof Error ? error.message : 'externalDataLayersLoadFailed',
				})
			);
		} finally {
			setLoading(false);
		}
	}, [dispatch]);

	useEffect(() => {
		if (showPanel && !config) {
			void load();
		}
	}, [showPanel, config, load]);

	const optionsFor = (layer: ExternalDataLayer): string[] => {
		return sortProvidersLiveFirst(config?.capabilities?.[layer] ?? []);
	};

	const handleEnabled = (layer: ExternalDataLayer, enabled: boolean): void => {
		setDraft((prev) => ({
			...prev,
			[layer]: {
				...(prev[layer] ?? {}),
				enabled,
			},
		}));
	};

	const handlePrimary = (layer: ExternalDataLayer, value: string): void => {
		setDraft((prev) => ({
			...prev,
			[layer]: {
				...(prev[layer] ?? {}),
				primaryProvider: value || null,
			},
		}));
	};

	const handleSecondary = (layer: ExternalDataLayer, value: string): void => {
		setDraft((prev) => ({
			...prev,
			[layer]: {
				...(prev[layer] ?? {}),
				secondaryProvider: value || null,
			},
		}));
	};

	const handleSave = async (): Promise<void> => {
		setSaving(true);
		try {
			const next = await patchExternalDataLayerConfig({
				layers: draft,
				oddsRefreshWithinHours,
			});
			setConfig(next);
			setDraft(next.layers ?? {});
			setOddsRefreshWithinHours(
				next.oddsRefreshWithinHours != null && next.oddsRefreshWithinHours > 0
					? next.oddsRefreshWithinHours
					: DEFAULT_ODDS_REFRESH_WITHIN_HOURS
			);
			setShowPanel(false);
			dispatch(showSuccessSnackbar({ message: t('externalDataLayersSaved') }));
		} catch (error) {
			dispatch(
				showErrorSnackbar({
					message: error instanceof Error ? error.message : 'externalDataLayersSaveFailed',
				})
			);
		} finally {
			setSaving(false);
		}
	};

	return (
		<AdminSection
			title={t('externalDataLayersTitle')}
			hint={showPanel ? t('externalDataLayersHint') : undefined}
		>
			<CustomButton
				sx={{ width: '100%', mb: showPanel ? 1.5 : 0 }}
				onClick={() => setShowPanel(!showPanel)}
				buttonColor="info"
				buttonVariant="outlined"
				buttonText={
					showPanel ? t('hideExternalDataLayers') : t('showExternalDataLayers')
				}
			/>

			{showPanel &&
				(loading || !config ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
						<CircularProgress size={28} />
					</Box>
				) : (
					<>
						{LAYERS.map((layer) => {
							const options = optionsFor(layer);
							const assignment = draft[layer] ?? {};
							const enabled = assignment.enabled !== false;
							const primary = assignment.primaryProvider ?? NONE;
							const secondary = assignment.secondaryProvider ?? NONE;
							const safePrimary = options.includes(primary) ? primary : NONE;
							const safeSecondary = options.includes(secondary) ? secondary : NONE;
							return (
								<Box key={layer} sx={layerCardSx(layer)}>
									<Typography
										sx={{
											fontWeight: 800,
											mb: 0.75,
											fontSize: '0.9rem',
											color: EXTERNAL_DATA_LAYER_ACCENT[layer],
											letterSpacing: '0.02em',
										}}
									>
										{t(LAYER_LABEL_KEY[layer])}
									</Typography>
									<Box sx={[toggleInlineRowSx, { mb: 1 }] as SxProps<Theme>}>
										<Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
											{t('externalDataLayerEnabled')}
										</Typography>
										<CustomSwitch
											checked={enabled}
											onChange={(e) => handleEnabled(layer, e.target.checked)}
											inputProps={{ 'aria-label': t('externalDataLayerEnabled') }}
										/>
									</Box>
									<FormControl fullWidth size="small" sx={{ mb: 1 }}>
										<InputLabel id={`${layer}-primary`}>{t('externalDataPrimary')}</InputLabel>
										<Select
											labelId={`${layer}-primary`}
											label={t('externalDataPrimary')}
											value={safePrimary}
											onChange={(e: SelectChangeEvent) => handlePrimary(layer, e.target.value)}
											renderValue={(v) =>
												v === NONE
													? t('externalDataProviderNone')
													: renderProviderSelectValue()(v)
											}
											sx={providerSelectSx}
										>
											<MenuItem value={NONE}>{t('externalDataProviderNone')}</MenuItem>
											{ProviderSelectItems({ providers: options })}
										</Select>
									</FormControl>
									<FormControl fullWidth size="small" sx={{ mb: layer === 'ODDS' ? 1 : 0 }}>
										<InputLabel id={`${layer}-secondary`}>{t('externalDataSecondary')}</InputLabel>
										<Select
											labelId={`${layer}-secondary`}
											label={t('externalDataSecondary')}
											value={safeSecondary}
											onChange={(e: SelectChangeEvent) => handleSecondary(layer, e.target.value)}
											renderValue={(v) =>
												v === NONE
													? t('externalDataProviderNone')
													: renderProviderSelectValue()(v)
											}
											sx={providerSelectSx}
										>
											<MenuItem value={NONE}>{t('externalDataProviderNone')}</MenuItem>
											{ProviderSelectItems({ providers: options })}
										</Select>
									</FormControl>
									{layer === 'ODDS' && (
										<TextField
											fullWidth
											size="small"
											type="number"
											label={t('externalDataOddsRefreshWithinHours')}
											helperText={t('externalDataOddsRefreshWithinHoursHint')}
											value={oddsRefreshWithinHours}
											onChange={(e) => {
												const raw = Number(e.target.value);
												if (!Number.isFinite(raw)) {
													return;
												}
												setOddsRefreshWithinHours(Math.trunc(raw));
											}}
											inputProps={{ min: 1, max: 168, step: 1 }}
										/>
									)}
								</Box>
							);
						})}
						<CustomSuccessButton
							onClick={() => void handleSave()}
							disabled={saving}
							loading={saving}
							buttonText={t('btnText.save')}
							sx={{ width: '100%', mr: 0 }}
						/>
					</>
				))}
		</AdminSection>
	);
}
